import { defineStore } from 'pinia'
import { useUserStore } from '~/stores/user'
import { useWorkspaceStore } from '~/stores/workspace'
import type { UserPin, UserPinType } from '~/types/UserPin'
import { UserPinsService } from '~/services/api/user/user-pins'

export const useUserPinsStore = defineStore('userPins', () => {
  const userPinsService = new UserPinsService()
  const { addToast } = useToastNotifications()

  const userStore = useUserStore()
  const workspaceStore = useWorkspaceStore()

  const userId = computed(() => userStore.user?.id ?? null)
  const workspaceId = computed(() => workspaceStore.currentWorkspaceId)

  const userPins = ref<UserPin[]>([])

  const workspacePins = computed(() => {
    if (!workspaceId.value) return []
    return userPins.value.filter((pin) => pin.workspaceId === workspaceId.value)
  })

  const MAX_PINS = 5

  const maxedOut = computed(() => {
    if (!workspaceId.value) return false
    return workspacePins.value.length >= MAX_PINS
  })

  async function fetchUserPins(userId: string) {
    const { data, error } = await userPinsService.getUserPins(userId)

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error fetching pins',
        message: 'Pins could not be fetched. Please try again.',
        life: 5000,
      })
      return
    }

    userPins.value = data
  }

  async function deleteUserPin(pinId: string) {
    // Optimistic update

    if (!userId.value) {
      captureError(
        new Error('Atttempted to delete pin but no user id was found'),
      )
      addToast({
        type: 'error',
        heading: 'Error deleting pin',
        message: 'The pin could not be removed. Please try again.',
        life: 5000,
      })
      return
    }

    const pinIndex = userPins.value.findIndex((pin) => pin.id === pinId)

    if (pinIndex === -1) return

    const pin = userPins.value[pinIndex]

    userPins.value.splice(pinIndex, 1)

    const { error } = await userPinsService.deleteUserPin(userId.value, pinId)

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error deleting pin',
        message: 'The pin could  not be removed. Please try again.',
        life: 5000,
      })

      userPins.value.splice(pinIndex, 0, pin)
    }
  }

  async function addUserPin(type: UserPinType, title: string, path: string) {
    if (!userId.value) {
      captureError(new Error('Atttempted to add pin but no user id was found'))
      addToast({
        type: 'error',
        heading: 'Error adding pin',
        message: 'The pin could not be added. Please try again.',
        life: 5000,
      })
      return
    }

    if (!workspaceId.value) {
      captureError(
        new Error('Atttempted to add pin but no workspace id was found'),
      )
      addToast({
        type: 'error',
        heading: 'Error adding pin',
        message: 'The pin could not be added. Please try again.',
        life: 5000,
      })
      return
    }

    const newPin: UserPin = {
      id: getNewId(),
      userId: userId.value,
      workspaceId: workspaceId.value,
      path,
      type,
      title,
    }

    userPins.value.push(newPin)

    const { error } = await userPinsService.postUserPin(userId.value, newPin)

    if (error !== null) {
      addToast({
        type: 'error',
        heading: 'Error adding pin',
        message: 'The pin could not be added. Please try again.',
        life: 5000,
      })

      userPins.value.pop()
    }
  }

  function reset() {
    userPins.value = []
  }

  const { $isInitialised, $isInitialising, $ensureInitialised } =
    useStoreManager(
      computed(() => userId.value),

      async () => {
        reset()

        if (!userId.value) return false

        await fetchUserPins(userId.value)

        return true
      },

      reset,
    )

  return {
    userPins,
    workspacePins,
    fetchUserPins,
    deleteUserPin,
    addUserPin,
    $isInitialised,
    $isInitialising,
    $ensureInitialised,
    maxedOut,
  }
})
